import { Fragment, useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import api from '@/lib/api';
import { toast } from '@/components/ui/sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ExternalLink, Check, X, ChevronDown, ChevronUp } from 'lucide-react';

const DISPLAY_STATUS_LABEL_KEY = {
  new: 'dash.pipeline.new',
  interested: 'dash.pipeline.interested',
  declined: 'dash.pipeline.declined',
  visit_requested: 'dash.pipeline.visitRequested',
  visit_scheduled: 'dash.pipeline.visitScheduled',
  withdrawn: 'dash.pipeline.withdrawn',
};

const PIPELINE_LABEL_KEY = {
  new: 'dash.pipeline.new',
  email_sent: 'dash.pipeline.emailSent',
  email_opened: 'dash.pipeline.emailOpened',
  matches_page_opened: 'dash.pipeline.matchesOpened',
  listing_viewed: 'dash.pipeline.listingViewed',
  interested: 'dash.pipeline.interested',
  visit_requested: 'dash.pipeline.visitRequested',
  visit_scheduled: 'dash.pipeline.visitScheduled',
  declined: 'dash.pipeline.declined',
  withdrawn: 'dash.pipeline.withdrawn',
};

function pipelineBadgeClass(stage) {
  if (stage === 'interested' || stage === 'visit_requested' || stage === 'visit_scheduled') {
    return 'bg-emerald-50 text-emerald-700';
  }
  if (stage === 'declined' || stage === 'withdrawn') return 'bg-slate-100 text-slate-600';
  if (stage === 'new') return 'bg-blue-50 text-blue-700';
  return 'bg-amber-50 text-amber-800';
}

export default function MatchesPipelineTable({
  proposals,
  onProposalUpdate,
  respondingProposalId,
  onRespondStart,
  onRespondEnd,
}) {
  const { t } = useLanguage();
  const [expandedId, setExpandedId] = useState('');
  const [availabilityProposalId, setAvailabilityProposalId] = useState('');
  const [availabilityDraft, setAvailabilityDraft] = useState('');

  const handleViewDetails = async (proposalId) => {
    const next = expandedId === proposalId ? '' : proposalId;
    setExpandedId(next);
    if (next) {
      try {
        await api.patch(`/listing-proposals/${proposalId}/track-view`);
      } catch {
        /* non-blocking */
      }
    }
  };

  const handleProposalResponse = async (proposalId, reply, availability = null) => {
    onRespondStart(proposalId);
    try {
      const payload = { status: reply };
      if (reply === 'interested' && typeof availability === 'string') {
        payload.availability = availability;
      }
      const res = await api.patch(`/listing-proposals/${proposalId}/respond`, payload);
      onProposalUpdate(proposalId, res.data);
      if (reply === 'interested') {
        toast.success(t('dash.proposalVisitSubmitted'));
        setAvailabilityProposalId('');
        setAvailabilityDraft('');
        setExpandedId('');
      } else {
        toast.success(t('dash.proposalDeclineDone'));
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('dash.proposalError'));
    } finally {
      onRespondEnd();
    }
  };

  if (!proposals.length) return null;

  return (
    <div className="bg-white rounded-lg border border-[#2B2B2B]/10 shadow-sm overflow-hidden" data-testid="matches-pipeline-table">
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-[#F5F3EF] text-[10px] uppercase tracking-wider text-[#5a5a5a]">
            <tr>
              <th className="text-left px-3 py-2.5 font-semibold">{t('dash.colProperty')}</th>
              <th className="text-left px-3 py-2.5 font-semibold">{t('dash.colAddress')}</th>
              <th className="text-left px-3 py-2.5 font-semibold">{t('dash.colRent')}</th>
              <th className="text-center px-3 py-2.5 font-semibold">{t('dash.colRooms')}</th>
              <th className="text-center px-3 py-2.5 font-semibold">{t('dash.colSurface')}</th>
              <th className="text-left px-3 py-2.5 font-semibold">{t('dash.colStatus')}</th>
              <th className="text-right px-3 py-2.5 font-semibold">{t('dash.colActions')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#2B2B2B]/5">
            {proposals.map((p) => {
              const apt = p.apartment || {};
              const displayStage = p.display_status || p.pipeline_stage || (p.status === 'proposed' ? 'new' : p.status);
              const labelKey = DISPLAY_STATUS_LABEL_KEY[displayStage] || PIPELINE_LABEL_KEY[displayStage] || 'dash.pipeline.new';
              const isExpanded = expandedId === p.id;
              const isPending = p.status === 'proposed';
              const isWorking = respondingProposalId === p.id;
              const showAvailability = availabilityProposalId === p.id;
              return (
                <Fragment key={p.id}>
                  <tr className="hover:bg-[#F5F3EF]/50" data-testid={`pipeline-row-${p.id}`}>
                    <td className="px-3 py-2 font-medium text-[#2B2B2B] max-w-[140px] truncate">{apt.title || '—'}</td>
                    <td className="px-3 py-2 text-xs text-[#5a5a5a] max-w-[160px] truncate">{apt.address || '—'}</td>
                    <td className="px-3 py-2 tabular-nums whitespace-nowrap">
                      {apt.rent != null ? `CHF ${Math.round(Number(apt.rent)).toLocaleString()}` : '—'}
                    </td>
                    <td className="px-3 py-2 text-center tabular-nums">{apt.rooms ?? '—'}</td>
                    <td className="px-3 py-2 text-center tabular-nums">{apt.size != null ? `${apt.size}m²` : '—'}</td>
                    <td className="px-3 py-2">
                      <Badge className={`${pipelineBadgeClass(displayStage)} border-0 text-[10px]`}>{t(labelKey)}</Badge>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs border-[#0B3D2E] text-[#0B3D2E]"
                        onClick={() => handleViewDetails(p.id)}
                        data-testid={`pipeline-view-${p.id}`}
                      >
                        {isExpanded ? <ChevronUp className="w-3 h-3 mr-1" /> : <ChevronDown className="w-3 h-3 mr-1" />}
                        {t('dash.viewDetails')}
                      </Button>
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr key={`${p.id}-detail`} className="bg-[#F5F3EF]/30">
                      <td colSpan={7} className="px-4 py-4">
                        <div className="space-y-3 text-sm max-w-2xl">
                          {apt.description && (
                            <p className="text-[#2B2B2B] whitespace-pre-wrap">{apt.description}</p>
                          )}
                          <div className="flex flex-wrap gap-3 text-xs text-[#5a5a5a]">
                            {apt.furnished != null && (
                              <span>{apt.furnished ? t('dash.furnished') : t('dash.unfurnished')}</span>
                            )}
                            {apt.neighborhood && <span>{apt.neighborhood}</span>}
                          </div>
                          {apt.external_url && (
                            <a
                              href={apt.external_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-xs text-[#0B3D2E] font-medium underline"
                            >
                              <ExternalLink className="w-3 h-3" /> {t('dash.viewListing')}
                            </a>
                          )}
                          {p.message && (
                            <p className="text-xs italic bg-white border border-[#2B2B2B]/10 rounded p-2">"{p.message}"</p>
                          )}
                          {p.availability && (
                            <p className="text-xs text-emerald-800 bg-emerald-50 rounded p-2 whitespace-pre-wrap">{p.availability}</p>
                          )}
                          {isPending && !showAvailability && (
                            <div className="flex gap-2 pt-1">
                              <Button
                                size="sm"
                                className="bg-[#0B3D2E] text-white text-xs"
                                disabled={isWorking}
                                onClick={() => { setAvailabilityProposalId(p.id); setAvailabilityDraft(''); }}
                              >
                                <Check className="w-3 h-3 mr-1" /> {t('dash.proposalInterested')}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-xs"
                                disabled={isWorking}
                                onClick={() => handleProposalResponse(p.id, 'declined')}
                              >
                                <X className="w-3 h-3 mr-1" /> {t('dash.proposalDecline')}
                              </Button>
                            </div>
                          )}
                          {isPending && showAvailability && (
                            <div className="space-y-2 border-t border-[#2B2B2B]/10 pt-3">
                              <Label className="text-xs font-medium">{t('dash.visitAvailabilityLabel')}</Label>
                              <Textarea
                                rows={3}
                                value={availabilityDraft}
                                onChange={(e) => setAvailabilityDraft(e.target.value)}
                                disabled={isWorking}
                                placeholder={t('dash.visitAvailabilityPlaceholder')}
                              />
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  className="bg-[#0B3D2E] text-white text-xs"
                                  disabled={isWorking || !availabilityDraft.trim()}
                                  onClick={() => handleProposalResponse(p.id, 'interested', availabilityDraft.trim())}
                                >
                                  {t('dash.submitAvailability')}
                                </Button>
                                <Button size="sm" variant="ghost" className="text-xs" onClick={() => setAvailabilityProposalId('')}>
                                  {t('common.cancel')}
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
